import { useEffect, useState } from "react";
import { Wallet, MapPin, Calendar as CalendarIcon, ArrowRight, Info, FileText, Plane, Download } from "lucide-react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { toast } from "sonner";

const SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
const fmtCur = (bucket) => {
  const entries = Object.entries(bucket || {}).filter(([, v]) => (v || 0) > 0);
  if (entries.length === 0) return "—";
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([c, v]) => `${SYM[c] || c}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`)
    .join(" · ");
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

// "2026-08-15" → "Aug 2026" — used for the small month pill on each row so
// partners can group Paid/Due status by month at a glance.
const fmtMonthTag = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

/**
 * Partner Portal — "Amount to pay" all-time overview + upcoming weekend list.
 *
 * Mounted on the Home tab beneath the monthly summary.
 */
export default function PartnerAmountDue({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/partner-portal/home-summary")
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const total = data?.total_amount_to_pay || {};
  const prog = data?.program_amount || {};
  const wknd = data?.weekend_amount || {};
  const upcoming = data?.upcoming_weekend_bookings || [];
  const programParticipants = data?.program_participants || [];
  const invoiceable = data?.invoiceable_bookings || [];
  const hasAny = Object.values(total).some(v => (v || 0) > 0);

  return (
    <div className="space-y-4" data-testid="partner-amount-due">
      {hasAny && (
        <div className="aii-card p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="pdue-total-card">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
              <Wallet size={11}/> Amount to pay · all-time
            </div>
            <div className="text-xs text-[color:var(--aii-muted)] mt-0.5">
              Sum of every priced program + weekend booking on your account.
            </div>
          </div>
          <div className="text-3xl font-display font-semibold text-brand" data-testid="pdue-total">
            {loading ? "…" : fmtCur(total)}
          </div>
          <button
            data-testid="pdue-arrivals-pdf"
            onClick={async () => {
              try {
                const resp = await fetch(`${API}/partner-portal/arrivals.pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
                if (!resp.ok) throw new Error(resp.status === 404 ? "No upcoming arrivals" : "Download failed");
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank", "noopener");
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              } catch (e) { toast.error(String(e.message || e)); }
            }}
            title="Download PDF of every student arriving this month + beyond"
            className="text-xs px-3 py-2 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5">
            <Download size={12}/> Arrivals PDF
          </button>
        </div>
      )}
      {!loading && !hasAny && (
        <div className="aii-card p-4 text-sm text-[color:var(--aii-muted)] italic flex items-start gap-2" data-testid="pdue-empty">
          <Info size={13} className="mt-0.5 flex-shrink-0"/>
          No priced bookings on your account yet. Amounts appear as soon as our team approves a program application or a weekend tour.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="aii-card p-5" data-testid="pdue-program-card">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest text-sky-700 font-bold flex items-center gap-1">
            <Plane size={11}/> Program bookings
          </div>
          <button
            type="button"
            onClick={() => onNavigate?.("my-applications")}
            className="text-[10px] font-semibold text-brand hover:underline flex items-center gap-0.5"
            data-testid="pdue-open-program"
          >
            Open <ArrowRight size={10}/>
          </button>
        </div>
        <div className="text-xs text-[color:var(--aii-muted)] mb-3">
          Every priced program booking on your account — earliest first.
        </div>
        <div className="text-2xl font-display font-semibold text-sky-800 mb-3" data-testid="pdue-program">
          {loading ? "…" : fmtCur(prog)}
        </div>
        {loading ? (
          <div className="text-sm text-[color:var(--aii-muted)] italic">Loading…</div>
        ) : programParticipants.length === 0 ? (
          <div className="text-sm text-[color:var(--aii-muted)] italic" data-testid="pdue-program-empty">
            No priced program bookings yet.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="pdue-program-list">
            {programParticipants.map((p) => (
              <li key={p.id} data-testid={`pdue-program-${p.id}`} className="border border-[color:var(--aii-border)] rounded-md p-2.5 text-sm flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="font-semibold truncate">{p.customer}</div>
                    {p.amount > 0 && (
                      <span data-testid={`pdue-program-tag-${p.id}`}
                            className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${Number(p.balance ?? p.amount) <= 0.5 ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-amber-100 text-amber-800 border border-amber-300"}`}>
                        {Number(p.balance ?? p.amount) <= 0.5 ? "✓ Paid" : "Due"}
                      </span>
                    )}
                    {p.start_date && (
                      <span data-testid={`pdue-program-month-${p.id}`}
                            className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-300"
                            title="Programme month">
                        {fmtMonthTag(p.start_date)}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[color:var(--aii-muted)] truncate">{p.programme}</div>
                  {p.start_date && (
                    <div className="text-[10px] text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1">
                      <CalendarIcon size={9}/> {fmtDate(p.start_date)}
                    </div>
                  )}
                </div>
                {p.amount > 0 && (
                  <div className="text-sm font-semibold text-sky-800 whitespace-nowrap text-right" data-testid={`pdue-program-price-${p.id}`}>
                    {SYM[p.currency] || p.currency}{Number(p.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    {p.paid > 0 && Number(p.balance ?? p.amount) > 0.5 && (
                      <div className="text-[10px] font-normal text-amber-700">
                        {SYM[p.currency] || p.currency}{Number(p.paid).toLocaleString(undefined, { maximumFractionDigits: 0 })} paid · {SYM[p.currency] || p.currency}{Number(p.balance).toLocaleString(undefined, { maximumFractionDigits: 0 })} due
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="aii-card p-5" data-testid="pdue-upcoming-card">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1">
            <MapPin size={11}/> Upcoming weekend bookings
          </div>
          <button
            type="button"
            onClick={() => onNavigate?.("weekend")}
            className="text-[10px] font-semibold text-brand hover:underline flex items-center gap-0.5"
            data-testid="pdue-open-weekend"
          >
            Open <ArrowRight size={10}/>
          </button>
        </div>
        <div className="text-xs text-[color:var(--aii-muted)] mb-3">
          Every scheduled weekend booked under your account — soonest first.
        </div>
        <div className="text-2xl font-display font-semibold text-emerald-800 mb-3" data-testid="pdue-weekend">
          {loading ? "…" : fmtCur(wknd)}
        </div>
        {loading ? (
          <div className="text-sm text-[color:var(--aii-muted)] italic">Loading…</div>
        ) : upcoming.length === 0 ? (
          <div className="text-sm text-[color:var(--aii-muted)] italic" data-testid="pdue-upcoming-empty">
            No upcoming weekend bookings scheduled.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="pdue-upcoming-list">
            {upcoming.map((u) => (
              <li key={u.id} data-testid={`pdue-upcoming-${u.id}`} className="border border-[color:var(--aii-border)] rounded-md p-2.5 text-sm flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="font-semibold truncate">{u.customer}</div>
                    {u.amount > 0 && (
                      <span data-testid={`pdue-upcoming-tag-${u.id}`}
                            className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${Number(u.amount) - Number(u.paid || 0) <= 0.5 ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-amber-100 text-amber-800 border border-amber-300"}`}>
                        {Number(u.amount) - Number(u.paid || 0) <= 0.5 ? "✓ Paid" : "Due"}
                      </span>
                    )}
                    {(u.tour_date || u.start_dates) && (
                      <span data-testid={`pdue-upcoming-month-${u.id}`}
                            className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300"
                            title="Tour month">
                        {fmtMonthTag(u.tour_date || u.start_dates)}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[color:var(--aii-muted)] truncate">{u.tour_title}{u.destination ? ` · ${u.destination}` : ""}</div>
                  <div className="text-[10px] text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1">
                    <CalendarIcon size={9}/> {fmtDate(u.tour_date || u.start_dates)}
                  </div>
                </div>
                {u.amount > 0 && (
                  <div className="text-sm font-semibold text-emerald-800 whitespace-nowrap text-right" data-testid={`pdue-price-${u.id}`}>
                    {SYM[u.currency] || u.currency}{Number(u.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    {u.paid > 0 && Number(u.amount) - Number(u.paid) > 0.5 && (
                      <div className="text-[10px] font-normal text-amber-700">
                        {SYM[u.currency] || u.currency}{Number(u.paid).toLocaleString(undefined, { maximumFractionDigits: 0 })} paid
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      </div>
      {invoiceable.length > 0 && (
        <div className="aii-card p-5" data-testid="pdue-invoices-card">
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
              <FileText size={11}/> Invoice downloads
            </div>
            <span className="text-[10px] text-[color:var(--aii-muted)]">{invoiceable.length} priced booking{invoiceable.length === 1 ? "" : "s"}</span>
          </div>
          <div className="text-xs text-[color:var(--aii-muted)] mb-3">
            Grouped by month → Program then Weekend. Click Download to fetch the printable invoice.
          </div>
          {(() => {
            // Group by "Aug 2026" bucket → within each bucket, split into
            // Program + Weekend sub-lists. Chronological order (earliest
            // month first) so partners can file receipts sequentially.
            const buckets = new Map();
            for (const row of invoiceable) {
              const key = row.start_date || "";
              const label = fmtMonthTag(key) || "Undated";
              const sortKey = key ? key.slice(0, 7) : "9999-99";
              if (!buckets.has(label)) buckets.set(label, { sortKey, program: [], weekend: [] });
              const b = buckets.get(label);
              (row.kind === "weekend" ? b.weekend : b.program).push(row);
            }
            const groups = [...buckets.entries()].sort((a, b) => a[1].sortKey.localeCompare(b[1].sortKey));
            const renderRow = (row) => (
              <li key={`inv-${row.kind}-${row.id}`} data-testid={`pdue-invoice-${row.id}`}
                  className="border border-[color:var(--aii-border)] rounded-md p-3 flex items-start justify-between gap-2 bg-white">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-sm truncate">{row.customer}</span>
                    {Number(row.balance ?? row.amount) <= 0.5 ? (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">✓ Paid</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">Due</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[color:var(--aii-muted)] truncate mt-0.5">{row.programme}</div>
                  {row.start_date && <div className="text-[10px] text-[color:var(--aii-muted)] mt-0.5 flex items-center gap-1"><CalendarIcon size={9}/>{fmtDate(row.start_date)}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-sm font-semibold text-brand whitespace-nowrap">
                    {SYM[row.currency] || row.currency}{Number(row.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                  <a href={`${API}/invoice/${row.id}?token=${getToken()}`} target="_blank" rel="noopener noreferrer"
                    data-testid={`pdue-invoice-download-${row.id}`}
                    className="text-[10px] px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold whitespace-nowrap flex items-center gap-1"
                    title="Open the printable invoice in a new tab">
                    <FileText size={9}/> Download
                  </a>
                </div>
              </li>
            );
            return (
              <div className="space-y-4" data-testid="pdue-invoices-list">
                {groups.map(([label, b]) => (
                  <section key={label} data-testid={`pdue-invoice-month-${label.replace(/\s+/g, "-")}`}>
                    <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white/80 backdrop-blur-sm py-1">
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-brand text-white">{label}</span>
                      <span className="text-[10px] text-[color:var(--aii-muted)]">
                        {b.program.length + b.weekend.length} booking{b.program.length + b.weekend.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    {b.program.length > 0 && (
                      <div className="mb-2" data-testid={`pdue-invoice-program-${label.replace(/\s+/g, "-")}`}>
                        <div className="text-[9px] uppercase tracking-widest text-sky-700 font-bold mb-1">✈️ Program bookings · {b.program.length}</div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {b.program.map(renderRow)}
                        </ul>
                      </div>
                    )}
                    {b.weekend.length > 0 && (
                      <div data-testid={`pdue-invoice-weekend-${label.replace(/\s+/g, "-")}`}>
                        <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold mb-1">🗺️ Weekend bookings · {b.weekend.length}</div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {b.weekend.map(renderRow)}
                        </ul>
                      </div>
                    )}
                  </section>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
